import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { Popover } from "./Popover";
import { groupBookingCodeRules } from "./bookingCodeRules";
import type { BookingCodeRule } from "../api/contracts/booking-code";

/**
 * Presentational grouped rule list (title + per-group subheadings). Shared by
 * the desktop click-popover (`BookingCodeRulesTooltip`) and the mobile inline
 * card (`MobileBookingCodeBlock`) so both render the rules identically.
 * Returns null when there is nothing to show.
 */
export const BookingCodeRulesList: React.FC<{ rules: BookingCodeRule[] }> = ({
  rules,
}) => {
  const { t } = useTranslation();
  const groups = groupBookingCodeRules(rules, t);

  if (groups.length === 0) return null;

  return (
    <div data-testid="booking-code-rules-content" className="text-left">
      <p className="mb-2 text-xs font-semibold text-ink">
        {t("booking_code.rules.title")}
      </p>
      <div className="flex flex-col gap-2">
        {groups.map((group) => (
          <div key={group.key} className="flex flex-col gap-0.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-ink/40">
              {group.title}
            </p>
            <ul className="flex flex-col gap-0.5">
              {group.lines.map((line, index) => (
                <li key={index} className="text-xs leading-snug text-ink/70">
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

interface BookingCodeRulesTooltipProps {
  rules: BookingCodeRule[];
  /** Trigger palette. `onDark` is for the black applied-code badge. */
  tone?: "default" | "onDark";
}

const TRIGGER_TONES = {
  default: "text-ink/60 hover:bg-surface-hover hover:text-ink",
  onDark: "text-on-primary/70 hover:bg-on-primary/25 hover:text-on-primary",
} as const;

/**
 * Click-to-toggle (i) popover that lists what the applied booking code
 * requires. Uses the click-driven `Popover` (works on mobile + desktop), not
 * the hover-only `Tooltip`. Callers gate rendering on
 * `configuration.showBookingCodeInfo`; a code with nothing to list renders no
 * icon at all rather than a dead one.
 */
export const BookingCodeRulesTooltip: React.FC<
  BookingCodeRulesTooltipProps
> = ({ rules, tone = "default" }) => {
  const { t } = useTranslation();

  const groups = groupBookingCodeRules(rules, t);

  if (groups.length === 0) return null;

  const trigger = (
    <button
      type="button"
      aria-label={t("booking_code.rules.aria_label")}
      data-testid="booking-code-rules-trigger"
      className={clsx(
        "flex h-5 w-5 items-center justify-center rounded-full transition-colors",
        TRIGGER_TONES[tone],
      )}
    >
      <FiInfo className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <Popover
      placement="bottom"
      className="!min-w-[220px] !max-w-[280px] !rounded-2xl !p-3"
      trigger={trigger}
      content={<BookingCodeRulesList rules={rules} />}
    />
  );
};
